import { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './RadioGlobeHeader.css';

const RadioGlobeMap = ({ markers = [], onMarkerClick, selectedMarker, mapStyle = 'dark', onMapStyleChange }) => {
    const [mapLoaded, setMapLoaded] = useState(false);
    const [styleLoaded, setStyleLoaded] = useState(false);
    const [popupShow, setPopupShow] = useState(false);
    const [popupCoordinates, setPopupCoordinates] = useState({ left: 0, top: 0 });
    const [popupDetails, setPopupDetails] = useState(null);
    const [markerInCircle, setMarkerInCircle] = useState(null); // Track marker currently in center circle
    const [markersInCircleCount, setMarkersInCircleCount] = useState(0); // Count of markers in circle
    const [mapInitialLoading, setMapInitialLoading] = useState(true); // Track initial map load for spinning animation
    // mapStyle is now a prop

    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const popupRef = useRef(null);
    const lastTriggeredMarkerRef = useRef(null); // Prevent re-triggering same marker
    const markerInCircleRef = useRef(null); // Keep ref in sync with state for event handlers
    const markersRef = useRef(markers); // Track latest markers array for event handlers
    const isViewingMarkerRef = useRef(false); // Track if we are currently viewing a specific marker to prevent spinning
    const skipZoomOnNextSelect = useRef(false); // Flag to skip zoom when triggered by center circle

    /**
     * Format markers into GeoJSON FeatureCollection
     */
    const formatMarkersFeatureCollection = (markers) => {
        const now = new Date();
        const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

        const featureCollection = {
            type: 'FeatureCollection',
            features: markers.map(marker => {
                const lat = marker.location?.coordinates?.[0] || 0;
                const lng = marker.location?.coordinates?.[1] || 0;

                // Check if marker was created within the last 24 hours
                const markerCreatedAt = marker.createdAt ? new Date(marker.createdAt) : null;
                const isRecent = markerCreatedAt ? markerCreatedAt >= twentyFourHoursAgo : false;

                return {
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [lng, lat], // [longitude, latitude] for GeoJSON
                    },
                    properties: {
                        ...marker,
                        id: marker._id,
                        title: marker.title,
                        address: marker.address,
                        isRecent: isRecent, // true if within 24h, false otherwise
                    },
                };
            }),
        };

        return featureCollection;
    };

    /**
     * Keep markersRef in sync with markers prop
     */
    useEffect(() => {
        markersRef.current = markers;
    }, [markers]);

    /**
     * Cycle through map styles: dark -> light -> satellite -> dark
     */
    const cycleMapStyle = () => {
        let newStyle;
        if (mapStyle === 'dark') {
            newStyle = 'light';
        } else if (mapStyle === 'light') {
            newStyle = 'satellite';
        } else {
            newStyle = 'dark';
        }

        if (onMapStyleChange) {
            onMapStyleChange(newStyle);
        }

        // Update map style
        if (mapRef.current) {
            const styleUrls = {
                dark: 'mapbox://styles/mapbox/dark-v11',
                light: 'mapbox://styles/mapbox/light-v11',
                satellite: 'mapbox://styles/mapbox/satellite-streets-v12'
            };

            // Reset styleLoaded to trigger marker re-addition
            setStyleLoaded(false);

            mapRef.current.setStyle(styleUrls[newStyle]);

            // Re-apply fog and reset styleLoaded after style loads
            mapRef.current.once('style.load', () => {
                mapRef.current.setFog({
                    range: [-1, 2],
                    'horizon-blend': 0.3,
                    color: newStyle === 'dark' ? '#141414' : newStyle === 'light' ? '#FFFFFF' : '#0B1026',
                    'high-color': newStyle === 'dark' ? '#141414' : newStyle === 'light' ? '#FFFFFF' : '#141414',
                    'space-color': '#0B1026',
                    'star-intensity': 0.5
                });

                // Set styleLoaded to true to trigger marker re-addition
                setStyleLoaded(true);
            });
        }
    };

    /**
     * Setup/initialize mapbox map
     */
    useEffect(() => {
        mapboxgl.accessToken = 'pk.eyJ1IjoibmhhbjEzMTk5OCIsImEiOiJjbTU5cjEwcHEwdTl5Mm1vOWg0ajB1aXF4In0.c07c-9Q_wzmye4OkxDA3sA';

        if (mapContainerRef.current && !mapRef.current) {
            // Construct style URL based on mapStyle
            const getStyleUrl = (style) => {
                if (style === 'satellite') {
                    return 'mapbox://styles/mapbox/satellite-streets-v12';
                }
                return `mapbox://styles/mapbox/${style}-v11`;
            };

            const map = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: getStyleUrl(mapStyle),
                projection: 'globe',
                zoom: 1.5,
                center: [105.8000, 21.0381], // Default to Hanoi
                pitch: 0, // Lock pitch to 0 for pure horizontal spinning
                bearing: 0, // Start facing north
                // Improved interaction settings
                scrollZoom: true,
                boxZoom: true,
                dragRotate: true,
                dragPan: true,
                keyboard: true,
                doubleClickZoom: true,
                touchZoomRotate: true,
                touchPitch: true,
                // Better zoom settings
                minZoom: 0.5,
                maxZoom: 20,
                minPitch: 0, // Prevent tilting down
                maxPitch: 30, // Limit tilt to 30 degrees to keep orientation clear
                // Cooperative gestures - prevents page zoom conflicts
                cooperativeGestures: false, // Set to false for direct control
            });

            mapRef.current = map;

            // Add zoom and rotation controls
            map.addControl(new mapboxgl.NavigationControl({
                // showCompass: true,
                // showZoom: true,
                showCompass: false,
                showZoom: false,
                visualizePitch: true
            }), 'top-right');

            // Prevent browser zoom when using scroll on map
            const mapContainer = mapContainerRef.current;

            const preventBrowserZoom = (e) => {
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                }
            };

            mapContainer.addEventListener('wheel', preventBrowserZoom, { passive: false });

            console.log('🎬 v7.0 RadioGlobeMap: Map initialization starting...');

            map.on('load', () => {
                setMapLoaded(true);
                console.log('🌍 v7.0 Radio Globe map loaded');
                // Stop spinning animation after 2 seconds
                setTimeout(() => {
                    setMapInitialLoading(false);
                }, 2000);

                // BATTERY SAVING: Auto-stop spinning after 10 seconds on mobile
                if (isMobile && !prefersReducedMotion) {
                    autoStopTimeout = setTimeout(() => {
                        console.log('🔋 Battery saver: Pausing globe spin after 10s');
                        spinEnabled = false;
                        stopSpinning();
                    }, 25000);
                }
            });

            map.on('style.load', () => {
                setStyleLoaded(true);

                // Determine initial fog color based on mapStyle prop
                const initialFogColor = mapStyle === 'dark' ? '#141414' : mapStyle === 'light' ? '#FFFFFF' : '#0B1026';
                const initialHighColor = mapStyle === 'dark' ? '#141414' : mapStyle === 'light' ? '#FFFFFF' : '#141414';

                // Set atmosphere/fog for globe effect
                map.setFog({
                    range: [-1, 2],
                    'horizon-blend': 0.3,
                    color: initialFogColor,
                    'high-color': initialHighColor,
                    'space-color': '#0B1026',
                    'star-intensity': 0.5
                });
            });

            // Globe spinning logic - only spins east-west (horizontally)
            // MOBILE PERFORMANCE OPTIMIZATIONS
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            // Slow down spinning on mobile to reduce heat and battery drain
            const spinIntervalMs = isMobile ? 1000 : 1000; // 1s on mobile vs 1s on desktop
            const secondsPerRevolution = isMobile ? 120 : 120; // Slower revolution on mobile

            const maxSpinZoom = 5;
            const slowSpinZoom = 3;
            let userInteracting = false;
            let spinEnabled = !prefersReducedMotion; // Respect accessibility preference
            let spinInterval = null;
            let resetTimeout = null;
            let autoStopTimeout = null;

            function spinGlobe() {
                const zoom = map.getZoom();
                // Don't spin if viewing a marker (drawer is showing marker details)
                if (spinEnabled && !userInteracting && zoom < maxSpinZoom && !isViewingMarkerRef.current) {
                    let distancePerSecond = 360 / secondsPerRevolution;
                    if (zoom > slowSpinZoom) {
                        const zoomDif = (maxSpinZoom - zoom) / (maxSpinZoom - slowSpinZoom);
                        distancePerSecond *= zoomDif;
                    }
                    const center = map.getCenter();
                    center.lng -= distancePerSecond;

                    // Reset pitch and bearing to keep spinning clean and horizontal
                    map.easeTo({
                        center,
                        pitch: 0,
                        bearing: 0,
                        duration: spinIntervalMs,
                        easing: (n) => n
                    });
                }
            }

            // Start continuous spinning
            function startSpinning() {
                if (spinInterval) clearInterval(spinInterval);
                spinInterval = setInterval(spinGlobe, spinIntervalMs);
            }

            // Stop continuous spinning
            function stopSpinning() {
                if (spinInterval) {
                    clearInterval(spinInterval);
                    spinInterval = null;
                }
            }

            // Reset orientation smoothly when user stops interacting
            const resetOrientation = () => {
                console.log('🔄 v7.0 resetOrientation called');
                // Clear any existing reset timeout
                if (resetTimeout) {
                    clearTimeout(resetTimeout);
                    resetTimeout = null;
                }

                // Debounce the reset to ensure user has finished interacting
                resetTimeout = setTimeout(() => {
                    userInteracting = false;
                    console.log('🔄 v7.0 resetOrientation executing... ViewingMarker:', isViewingMarkerRef.current);

                    // Only reset if not currently interacting
                    if (!userInteracting) {
                        // If we are viewing a marker, DO NOT reset orientation or spin
                        if (isViewingMarkerRef.current) {
                            console.log('🛑 v7.0 resetOrientation BLOCKED because viewing marker');
                            return;
                        }

                        console.log('🌪️ v7.0 resetOrientation performing easeTo');
                        // Smoothly reset pitch and bearing to 0 (horizontal view)
                        map.easeTo({
                            pitch: 0,
                            bearing: 0,
                            duration: 1500
                        });

                        // Resume spinning after reset based on zoom level
                        setTimeout(() => {
                            const zoom = map.getZoom();
                            // Only spin if NOT viewing a marker AND spinEnabled (not paused by battery saver)
                            if (!userInteracting && zoom < maxSpinZoom && !isViewingMarkerRef.current && spinEnabled) {
                                startSpinning();
                            }
                        }, 1600); // Slightly longer than easeTo duration
                    }
                }, 500); // Wait 500ms after interaction ends
            };

            // Handle zoom changes to start/stop spinning
            map.on('zoom', () => {
                const zoom = map.getZoom();
                if (!userInteracting) {
                    if (zoom < maxSpinZoom && !spinInterval) {
                        startSpinning();
                    } else if (zoom >= maxSpinZoom && spinInterval) {
                        stopSpinning();
                    }
                }
            });

            // User interaction handlers
            const handleInteractionStart = () => {
                console.log('✋ v7.0 handleInteractionStart');
                userInteracting = true;
                isViewingMarkerRef.current = false; // Reset viewing state on interaction
                stopSpinning();
                // Re-enable spinning on user interaction (resets battery saver)
                spinEnabled = !prefersReducedMotion;
                // Clear battery saver timeout
                if (autoStopTimeout) {
                    clearTimeout(autoStopTimeout);
                    autoStopTimeout = null;
                }
                // Clear any pending reset
                if (resetTimeout) {
                    clearTimeout(resetTimeout);
                    resetTimeout = null;
                }
            };

            map.on('mousedown', handleInteractionStart);
            map.on('touchstart', handleInteractionStart);
            map.on('dragstart', handleInteractionStart);

            map.on('mouseup', resetOrientation);
            // map.on('dragend', resetOrientation); // REMOVED - conflicts with center circle handler
            map.on('pitchend', resetOrientation);
            map.on('rotateend', resetOrientation);
            map.on('touchend', resetOrientation);

            // Center circle marker detection - check on map move
            const CIRCLE_RADIUS = 30; // Reduced size (60px diameter)

            const checkMarkersInCircle = () => {
                if (!mapRef.current || !markersRef.current || markersRef.current.length === 0) {
                    setMarkersInCircleCount(0);
                    return;
                }

                const map = mapRef.current;
                const mapContainer = map.getContainer();
                const centerX = mapContainer.offsetWidth / 2;
                const centerY = mapContainer.offsetHeight * 0.45; // Moved up to 45% height

                let closestMarker = null;
                let closestDistance = Infinity;
                let count = 0; // Count markers in circle

                // Check each marker's distance from center
                markersRef.current.forEach(marker => {
                    const lat = marker.location?.coordinates?.[0] || 0;
                    const lng = marker.location?.coordinates?.[1] || 0;
                    const point = map.project([lng, lat]);

                    const distance = Math.sqrt(
                        Math.pow(point.x - centerX, 2) + Math.pow(point.y - centerY, 2)
                    );

                    if (distance < CIRCLE_RADIUS) {
                        count++; // Increment count for any marker in circle
                        if (distance < closestDistance) {
                            closestDistance = distance;
                            closestMarker = marker;
                        }
                    }
                });

                // Update count state
                setMarkersInCircleCount(count);

                // Update marker in circle state
                if (closestMarker) {
                    setMarkerInCircle(closestMarker);
                    markerInCircleRef.current = closestMarker;
                } else {
                    setMarkerInCircle(null);
                    markerInCircleRef.current = null;
                }
            };

            // Combined drag end handler - handles both orientation reset AND center circle logic
            const handleCombinedDragEnd = () => {
                console.log('🚨 v7.0 DRAGEND EVENT FIRED! 🚨');

                // CRITICAL FIX: Run marker check IMMEDIATELY on dragend to get latest position
                // This fixes race condition where move event data might be stale
                checkMarkersInCircle();

                const markerToRecenter = markerInCircleRef.current;

                console.log('🎯 v7.0 Drag ended, marker in circle:', markerToRecenter);

                // Reset lastTriggeredMarkerRef if no marker in circle (allows re-selecting same marker after dragging away)
                if (!markerToRecenter) {
                    lastTriggeredMarkerRef.current = null;
                }

                if (markerToRecenter && markerToRecenter._id !== lastTriggeredMarkerRef.current) {
                    // CENTER CIRCLE LOGIC - recenter on marker
                    console.log('✅ v7.0 Triggering recenter for marker:', markerToRecenter._id);

                    const lat = markerToRecenter.location?.coordinates?.[0] || 0;
                    const lng = markerToRecenter.location?.coordinates?.[1] || 0;

                    // Set viewing state to TRUE to prevent spinning
                    isViewingMarkerRef.current = true;
                    // Cancel any pending reset orientation
                    if (resetTimeout) {
                        clearTimeout(resetTimeout);
                        resetTimeout = null;
                    }
                    stopSpinning();

                    // Get current zoom to maintain it
                    const currentZoom = map.getZoom();
                    console.log('📏 Current zoom BEFORE:', currentZoom);

                    // CRITICAL: Set flag BEFORE any map operations
                    skipZoomOnNextSelect.current = true;

                    // Use setTimeout to ensure state update completes before map animation
                    setTimeout(() => {
                        console.log('🗺️ v7.0 Starting easeTo animation with zoom:', currentZoom);

                        // FIX FOR GLOBE PROJECTION: Instead of using offset (which breaks on globe at low zoom),
                        // calculate the geographic center that will place the marker at the circle position (45% height)
                        const mapContainer = map.getContainer();
                        const mapHeight = mapContainer.offsetHeight;
                        const mapWidth = mapContainer.offsetWidth;

                        // Circle is at (50% width, 45% height), so marker needs to be at that position
                        // First, project the marker to screen coordinates
                        const markerScreenPoint = map.project([lng, lat]);

                        // Target position for marker: center of circle
                        const targetMarkerX = mapWidth / 2;  // 50% width
                        const targetMarkerY = mapHeight * 0.45;  // 45% height

                        // Calculate how much we need to shift the view
                        // If marker is to the right of target, we need to move center right (positive delta)
                        // If marker is below target, we need to move center down (positive delta)
                        const deltaX = markerScreenPoint.x - targetMarkerX;
                        const deltaY = markerScreenPoint.y - targetMarkerY;

                        // New center screen position = current center + deltas
                        const newCenterScreenX = (mapWidth / 2) + deltaX;
                        const newCenterScreenY = (mapHeight / 2) + deltaY;

                        // Unproject to get the geographic coordinates of the new center
                        const newCenter = map.unproject([newCenterScreenX, newCenterScreenY]);

                        console.log('🎯 Marker screen:', markerScreenPoint, 'Delta:', { deltaX, deltaY }, '-> New center:', [newCenter.lng, newCenter.lat]);

                        // Keep same zoom, recenter to place marker at circle position
                        map.easeTo({
                            center: [newCenter.lng, newCenter.lat],
                            zoom: currentZoom,
                            duration: 800,
                            essential: true
                        });

                        // Verify position after animation
                        setTimeout(() => {
                            const zoomAfter = map.getZoom();
                            console.log('📏 Zoom AFTER easeTo:', zoomAfter);
                        }, 100);
                    }, 0);

                    // Open marker in drawer
                    if (onMarkerClick) {
                        console.log('🚪 v7.0 Opening drawer for marker ID:', markerToRecenter._id);
                        console.log('🚪 v7.0 Full marker object:', markerToRecenter);
                        onMarkerClick(markerToRecenter._id);
                    } else {
                        console.warn('⚠️ onMarkerClick callback is not defined!');
                    }

                    lastTriggeredMarkerRef.current = markerToRecenter._id;

                    // Reset flag after animation completes
                    setTimeout(() => {
                        skipZoomOnNextSelect.current = false;
                        console.log('🔄 Reset skipZoomOnNextSelect flag');
                    }, 1500);
                } else {
                    // ORIENTATION RESET LOGIC - no marker in circle, do orientation reset
                    console.log('❌ No recenter:', !markerToRecenter ? 'No marker in circle' : 'Same marker already triggered');

                    // Call the original reset orientation logic
                    resetOrientation();
                }
            };

            // Attach event listeners
            map.on('move', checkMarkersInCircle);
            console.log('🎭 v7.0 Attaching dragend handler to map...');
            map.on('dragend', handleCombinedDragEnd); // Use combined handler
            console.log('✅ v7.0 Dragend handler attached successfully');

            // Start initial spinning
            startSpinning();

            return () => {
                if (mapRef.current) {
                    mapContainer.removeEventListener('wheel', preventBrowserZoom);
                    mapRef.current.remove();
                    mapRef.current = null;
                }
            };
        }
    }, []);

    /**
     * Add/update markers source and layer
     */
    useEffect(() => {
        if (mapLoaded && styleLoaded && markers && markers.length > 0) {
            const markersFeatureCollection = formatMarkersFeatureCollection(markers);
            const map = mapRef.current;

            if (map && !map.getSource('markers-source')) {
                // Add pulsing dot for selected marker
                const size = 200;
                const pulsingDot = {
                    width: size,
                    height: size,
                    data: new Uint8ClampedArray(size * size * 4),
                    context: null,

                    onAdd: function () {
                        const canvas = document.createElement('canvas');
                        canvas.width = this.width;
                        canvas.height = this.height;
                        this.context = canvas.getContext('2d');
                    },

                    render: function () {
                        const duration = 1000;
                        const t = (performance.now() % duration) / duration;
                        const radius = (size / 2) * 0.3;
                        const outerRadius = (size / 2) * 0.7 * t + radius;
                        const context = this.context;

                        if (context === null) {
                            mapRef?.current?.triggerRepaint();
                            return true;
                        }

                        context.clearRect(0, 0, this.width, this.height);
                        context.beginPath();
                        context.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2);
                        context.fillStyle = `rgba(100, 200, 255, ${1 - t})`;
                        context.fill();

                        context.beginPath();
                        context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
                        context.fillStyle = 'rgba(0, 150, 255, 1)';
                        context.strokeStyle = 'white';
                        context.lineWidth = 2 + 4 * (1 - t);
                        context.fill();
                        context.stroke();

                        this.data = context.getImageData(0, 0, this.width, this.height).data;
                        mapRef?.current?.triggerRepaint();

                        return true;
                    }
                };

                map.addImage('pulsing-dot', pulsingDot, { pixelRatio: 2 });

                // Add grey pulsing dot for older markers
                const greyPulsingDot = {
                    width: size,
                    height: size,
                    data: new Uint8ClampedArray(size * size * 4),
                    context: null,

                    onAdd: function () {
                        const canvas = document.createElement('canvas');
                        canvas.width = this.width;
                        canvas.height = this.height;
                        this.context = canvas.getContext('2d');
                    },

                    render: function () {
                        const duration = 1000;
                        const t = (performance.now() % duration) / duration;
                        const radius = (size / 2) * 0.3;
                        const outerRadius = (size / 2) * 0.7 * t + radius;
                        const context = this.context;

                        if (context === null) {
                            mapRef?.current?.triggerRepaint();
                            return true;
                        }

                        context.clearRect(0, 0, this.width, this.height);
                        context.beginPath();
                        context.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2);
                        context.fillStyle = `rgba(158, 158, 158, ${1 - t})`; // Grey outer pulse
                        context.fill();

                        context.beginPath();
                        context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
                        context.fillStyle = 'rgba(120, 120, 120, 1)'; // Grey center
                        context.strokeStyle = 'white';
                        context.lineWidth = 2 + 4 * (1 - t);
                        context.fill();
                        context.stroke();

                        this.data = context.getImageData(0, 0, this.width, this.height).data;
                        mapRef?.current?.triggerRepaint();

                        return true;
                    }
                };

                map.addImage('pulsing-dot-grey', greyPulsingDot, { pixelRatio: 2 });

                // Add markers source
                map.addSource('markers-source', {
                    type: 'geojson',
                    data: markersFeatureCollection,
                    promoteId: 'id'
                });

                // Add pulsing dot source (for selected marker)
                map.addSource('dot-point-source', {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: []
                    }
                });

                // Add markers layer (circle markers)
                map.addLayer({
                    id: 'markers-layer',
                    type: 'circle',
                    source: 'markers-source',
                    paint: {
                        'circle-radius': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            3, 3,
                            10, 6,
                            20, 10
                        ],
                        'circle-stroke-width': 2,
                        'circle-color': [
                            'case',
                            ['boolean', ['feature-state', 'hovered'], false], '#FFD700', // Gold on hover
                            ['boolean', ['get', 'isRecent'], false], '#007bff', // Blue for recent (within 24h)
                            '#9E9E9E' // Grey for older markers
                        ],
                        'circle-stroke-color': 'white'
                    },
                    layout: {
                        'circle-sort-key': 0 // Default sort key, will be updated dynamically (layout prop, not paint)
                    },
                });

                // Add pulsing dot layer
                map.addLayer({
                    id: 'layer-with-pulsing-dot',
                    type: 'symbol',
                    source: 'dot-point-source',
                    layout: {
                        'icon-image': 'pulsing-dot'
                    }
                });

                // Marker click event
                let hoveredMarkerId = undefined;

                map.on('click', 'markers-layer', (e) => {
                    if (e.features && e.features[0].properties) {
                        const clickedMarker = e.features[0].properties;
                        console.log('📍 Marker clicked:', clickedMarker);

                        // Parse the marker data (it's stringified in properties)
                        if (onMarkerClick) {
                            onMarkerClick(clickedMarker.id);
                        }
                    }
                });

                // Hover effects
                map.on('mouseenter', 'markers-layer', (e) => {
                    map.getCanvas().style.cursor = 'pointer';

                    if (e.features && e.features[0].properties && e.features[0].geometry.type === 'Point') {
                        const coordinates = e.features[0].geometry.coordinates.slice();
                        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                        }

                        const pixelPoint = map.project([coordinates[0], coordinates[1]]);
                        const popupTop = pixelPoint.y - ((popupRef.current?.offsetHeight || 0) / 2);
                        const popupLeft = pixelPoint.x + 20;
                        setPopupCoordinates({ top: popupTop, left: popupLeft });

                        const details = e.features[0].properties;
                        setPopupDetails(details);
                        setPopupShow(true);

                        hoveredMarkerId = e.features[0].id;
                        map.setFeatureState(
                            { source: 'markers-source', id: hoveredMarkerId },
                            { hovered: true }
                        );
                    }
                });

                map.on('mouseleave', 'markers-layer', () => {
                    map.getCanvas().style.cursor = '';
                    setPopupShow(false);
                    setPopupDetails(null);

                    if (hoveredMarkerId !== undefined) {
                        map.setFeatureState(
                            { source: 'markers-source', id: hoveredMarkerId },
                            { hovered: false }
                        );
                        hoveredMarkerId = undefined;
                    }
                });

            } else if (map && map.getSource('markers-source')) {
                // Update existing source
                const markersSource = map.getSource('markers-source');
                if (markersSource && markersSource.type === 'geojson') {
                    markersSource.setData(markersFeatureCollection);
                }
            }
        }
    }, [mapLoaded, styleLoaded, markers, onMarkerClick]);

    /**
     * Update marker color when in center circle
     */
    useEffect(() => {
        if (mapRef.current && mapLoaded && styleLoaded && markerInCircle) {
            const map = mapRef.current;

            // Highlight the marker in circle
            if (map.getLayer('markers-layer')) {
                // Make the marker in circle stand out with color
                map.setPaintProperty('markers-layer', 'circle-color', [
                    'case',
                    ['==', ['get', 'id'], markerInCircle._id],
                    '#FFD700', // Gold for marker in circle
                    [
                        'case',
                        ['boolean', ['feature-state', 'hovered'], false],
                        '#FFD700', // Gold on hover
                        ['boolean', ['get', 'isRecent'], false],
                        '#007bff', // Blue for recent (within 24h)
                        '#9E9E9E' // Grey for older markers
                    ]
                ]);

                // Make the marker in circle LARGER so it's visible above stacked markers
                map.setPaintProperty('markers-layer', 'circle-radius', [
                    'case',
                    ['==', ['get', 'id'], markerInCircle._id],
                    [
                        'interpolate', ['linear'], ['zoom'],
                        3, 6,   // 2x size at low zoom
                        10, 10, // bigger at mid zoom
                        20, 14  // bigger at high zoom
                    ],
                    [
                        'interpolate', ['linear'], ['zoom'],
                        3, 3,
                        10, 6,
                        20, 10
                    ]
                ]);

                // Thicker stroke for highlighted marker
                map.setPaintProperty('markers-layer', 'circle-stroke-width', [
                    'case',
                    ['==', ['get', 'id'], markerInCircle._id],
                    3, // Thicker stroke for marker in circle
                    2  // Normal stroke
                ]);

                // CRITICAL: Bring highlighted marker to TOP of z-order so it renders above stacked markers
                map.setLayoutProperty('markers-layer', 'circle-sort-key', [
                    'case',
                    ['==', ['get', 'id'], markerInCircle._id],
                    999, // High value = render on top
                    0    // Others stay at default
                ]);
            }
        } else if (mapRef.current && mapLoaded && styleLoaded && !markerInCircle) {
            // Reset to default colors and sizes when no marker in circle
            const map = mapRef.current;
            if (map.getLayer('markers-layer')) {
                map.setPaintProperty('markers-layer', 'circle-color', [
                    'case',
                    ['boolean', ['feature-state', 'hovered'], false],
                    '#FFD700', // Gold on hover
                    ['boolean', ['get', 'isRecent'], false],
                    '#007bff', // Blue for recent (within 24h)
                    '#9E9E9E' // Grey for older markers
                ]);

                // Reset radius to default
                map.setPaintProperty('markers-layer', 'circle-radius', [
                    'interpolate', ['linear'], ['zoom'],
                    3, 3,
                    10, 6,
                    20, 10
                ]);

                // Reset stroke width to default
                map.setPaintProperty('markers-layer', 'circle-stroke-width', 2);

                // Reset sort key (z-order) to default
                map.setLayoutProperty('markers-layer', 'circle-sort-key', 0);
            }
        }
    }, [markerInCircle, mapLoaded, styleLoaded]);

    /**
     * Update pulsing dot when selected marker changes
     */
    useEffect(() => {
        if (mapRef.current && selectedMarker && mapLoaded && styleLoaded) {
            const map = mapRef.current;
            const dotPointSource = map.getSource('dot-point-source');

            if (dotPointSource && dotPointSource.type === 'geojson') {
                const lat = selectedMarker.location?.coordinates?.[0] || 0;
                const lng = selectedMarker.location?.coordinates?.[1] || 0;

                // Check if selected marker is recent (within 24h)
                const now = new Date();
                const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                const markerCreatedAt = selectedMarker.createdAt ? new Date(selectedMarker.createdAt) : null;
                const isRecent = markerCreatedAt ? markerCreatedAt >= twentyFourHoursAgo : false;

                const pointFeature = {
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [lng, lat]
                    },
                    properties: {},
                };

                dotPointSource.setData(pointFeature);

                // Update pulsing dot icon based on marker age
                if (map.getLayer('layer-with-pulsing-dot')) {
                    map.setLayoutProperty('layer-with-pulsing-dot', 'icon-image', isRecent ? 'pulsing-dot' : 'pulsing-dot-grey');
                }

                // Only fly/zoom if NOT triggered by center circle
                if (!skipZoomOnNextSelect.current) {
                    console.log('🔍 v7.0 Flying to selected marker with zoom');
                    map.flyTo({
                        center: [lng, lat],
                        zoom: 12,
                        pitch: 0,
                        bearing: 0,
                        duration: 3000,
                        essential: true,
                    });
                } else {
                    console.log('⏭️ v7.0 Skipping zoom for center circle trigger');
                }
            }
        }
    }, [selectedMarker, mapLoaded, styleLoaded]); // Removed skipZoomOnNextSelect from dependencies

    return (
        <>
            <div
                id="radio-globe-map-container"
                ref={mapContainerRef}
                style={{
                    height: '100dvh',
                    width: '100vw',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                }}
            />

            {/* Popup on hover */}
            <div
                id="map-popup"
                ref={popupRef}
                style={{
                    position: 'absolute',
                    top: `${popupCoordinates.top}px`,
                    left: `${popupCoordinates.left}px`,
                    maxWidth: '300px',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    padding: '12px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    visibility: popupShow && popupDetails ? 'visible' : 'hidden',
                    pointerEvents: 'none',
                    zIndex: 10,
                }}
            >
                {popupDetails && (
                    <div style={{ fontFamily: "'Nunito', sans-serif" }}>
                        <h3 style={{ margin: 0, marginBottom: '4px', fontSize: '14px', fontWeight: 'bold' }}>
                            {popupDetails.title || 'Untitled'}
                        </h3>
                        <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                            {popupDetails.address || 'No address'}
                        </p>
                    </div>
                )}
            </div>

            {/* Center Circle Overlay */}
            {/* **here circle color here */}
            <div
                style={{
                    position: 'absolute',
                    top: '45%', // Moved up
                    left: '50%',
                    width: '60px', // 30px radius = 60px diameter
                    height: '60px',
                    transform: 'translate(-50%, -50%)',
                    borderRadius: '50%',
                    border: `2px ${mapInitialLoading ? 'dashed' : 'solid'} ${mapStyle === 'dark' ? 'rgba(255, 255, 255, 0.6)' :
                        mapStyle === 'light' ? 'rgba(196, 193, 193, 0.5)' :
                            'rgba(255, 255, 255, 0.7)'
                        }`,
                    pointerEvents: 'none',
                    zIndex: 10,
                    animation: mapInitialLoading ? 'spin 2s linear infinite' : 'none',
                }}
            >
                {/* Marker count badge - shows when markers are in circle */}
                {markersInCircleCount > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            minWidth: '20px',
                            height: '20px',
                            borderRadius: '10px',
                            backgroundColor: mapStyle === 'dark' ? 'rgba(255, 215, 0, 0.9)' :
                                mapStyle === 'light' ? 'rgba(0, 123, 255, 0.9)' :
                                    'rgba(255, 215, 0, 0.9)',
                            color: mapStyle === 'light' ? 'white' : '#333',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 6px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            fontFamily: "'Nunito', sans-serif",
                        }}
                    >
                        {markersInCircleCount}
                    </div>
                )}
            </div>

            {/* Header with style toggle */}
            <div className="radio-globe-header">
                <button onClick={cycleMapStyle} className="style-toggle-btn" title={`Current: ${mapStyle}. Click to cycle.`}>
                    {mapStyle === 'dark' ? '🌙' : mapStyle === 'light' ? '☀️' : '🛰️'}
                </button>
            </div>

            {/* Add spinning animation keyframes */}
            <style>{`
                @keyframes spin {
                    from { transform: translate(-50%, -50%) rotate(0deg); }
                    to { transform: translate(-50%, -50%) rotate(360deg); }
                }
            `}</style>
        </>
    );
};

export default RadioGlobeMap;
